import { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shallowEqual } from 'react-redux';

import { groupBy } from 'lodash';

import { Table, Button, TTableColumn, TextSkeleton, Divider } from '@treezor/design-system';

import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';

import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';

import { KycNeedReviewActions } from '@/pages/admin/kyc-need-review/Actions/KycNeedReviewActions';

import { getKycNeedReviewDocuments } from '@/features/kyc/store/actions/kyc-need-review.actions';

import { IKycDocument } from '@/features/kyc/store/models/kyc.model';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

import CursorNavigation from '@/ui-kit/cursor-navigation/CursorNavigation';

export const KycNeedReview = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const documents = useAppSelector(
    (state) =>
      state.kycNeedReview.documents?.elements
        ?.map((needReviewDocument) => needReviewDocument.state?.document as IKycDocument)
        ?.filter((document) => document.status !== 'VALIDATED'),
    shallowEqual,
  );
  const isLoading = useAppSelector((state) => state.kycNeedReview.isLoading);

  const rows = useMemo(() => {
    const documentsGroupedByUserId = groupBy(documents, 'userId');

    return Object.keys(documentsGroupedByUserId).map((userId) => ({
      userId,
      pendingDocuments: documentsGroupedByUserId[userId],
      uploaded: documentsGroupedByUserId[userId]?.length || 0,
    }));
  }, [documents]);

  type TData = (typeof rows)[number];

  const columns: TTableColumn<TData>[] = [
    {
      name: 'userId',
      field: 'userId',
      label: t('pages.administration.kycNeedReview.pages.table.userId'),
      cellRenderer: (item) =>
        isLoading ? (
          <TextSkeleton width="80px" margin="8px" />
        ) : (
          <Button variant="ghost" onClick={() => navigate(DashboardHelpers.generateProfilePath(item.userId))}>
            {item.userId}
          </Button>
        ),
    },
    {
      name: 'pendingDocuments',
      field: 'pendingDocuments',
      label: t('pages.administration.kycNeedReview.pages.table.pendingDoc'),
      cellRenderer: (item) =>
        isLoading ? (
          <TextSkeleton width="320px" margin="8px" />
        ) : (
          <div className="flex gap-2 flex-wrap">
            {item.pendingDocuments.map((document) => (
              <KycNeedReviewActions key={document.documentId} document={document} />
            ))}
          </div>
        ),
    },
    {
      name: 'uploaded',
      field: 'uploaded',
      label: t('pages.administration.kycNeedReview.pages.table.uploaded'),
      cellRenderer: (item) => (isLoading ? <TextSkeleton width="40px" margin="8px" /> : <span>{item.uploaded}</span>),
    },
  ];

  useEffect(() => {
    dispatch(getKycNeedReviewDocuments());
  }, [dispatch]);

  const cursor = useAppSelector((state) => state.kycNeedReview.documents?.cursor) || {};

  const handleCursor = useCallback(
    (cursor?: string) => {
      dispatch(getKycNeedReviewDocuments(cursor));
    },
    [dispatch],
  );

  return (
    <div className="flex flex-col gap-5 h-full p-6">
      <div className="flex flex-col overflow-auto">
        <Table
          columns={columns}
          rows={rows}
          className="bg-primary"
          emptyComponent={
            isLoading ? (
              new Array(12).fill(undefined).map((index) => (
                <>
                  <div key={`document-loader-${index}`} className="flex items-center px-5 py-3 gap-[120px]">
                    <TextSkeleton width="80px" margin="8px" />
                    <TextSkeleton width="320px" margin="8px" />
                    <TextSkeleton width="40px" margin="8px" />
                  </div>
                  <Divider />
                </>
              ))
            ) : (
              <EmptyState title={t('components.molecules.table.noRecord')} icon="search_v1" size="m" />
            )
          }
        />
        <CursorNavigation cursor={cursor} handleCursor={handleCursor} hasLabel />
      </div>
    </div>
  );
};
